import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EuiAccordion,
  EuiBadge,
  EuiBasicTable,
  EuiBasicTableColumn,
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiDescriptionList,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHealth,
  EuiHorizontalRule,
  EuiIcon,
  EuiLink,
  EuiPanel,
  EuiSpacer,
  EuiStat,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import { useApi } from "src/api";
import {
  NetBoxDeviceWithInventory,
  NetBoxInterface,
  NetBoxInventoryItem,
  OpsanioHardwareKind,
  useAnketaAuth,
} from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { AnketaResourcesEnum } from "src/auth/types";
import { useOpsanioCan } from "../../can";
import {
  HardwareTicketModal,
  HardwareTicketTarget,
  useCanOpenHardwareTicket,
} from "../../hardware/ticketModal";
import { FormSkeleton } from "src/components/skeleton";
import { CUSTOMER_SWITCH_VALUE } from "src/helper";
import {
  factText,
  formatCapacity,
  HARDWARE_KIND_ICON,
  HW_DAC_KEY,
  HW_SERVERS_KEY,
  HW_SWITCHES_KEY,
  HW_SWITCH_MAC_KEY,
  HW_SWITCH_MODEL_KEY,
  HW_SWITCH_SERIAL_KEY,
  parseDeviceIds,
} from "../hardware";
import { HardwareSyncButton } from "./hardwareSync";
import { SwitchConfigCopyButton } from "./switchConfig";

const i18n = "anketa.opsanio.appliance.flyout.hardware";

/**
 * The CPE uplink NICs are tagged during NetBox sync. The two spellings below
 * both occur in the estate (the sync writes `opsaniohardwarenicscpe`, older
 * data carries `opsanioniccpe`), so both count as "tagged".
 */
const CPE_TAG_SLUGS = ["opsaniohardwarenicscpe", "opsanioniccpe"];

const TEN_GB_TYPES = [
  "10gbase-t",
  "10gbase-x-sfpp",
  "10gbase-x-xfp",
  "10gbase-x-sfpplus",
  "10gbase-cx4",
  "10gbase-kr",
];

const STATUS_COLOR: Record<string, string> = {
  active: "success",
  planned: "warning",
  staged: "warning",
  inventory: "hollow",
  offline: "danger",
  failed: "danger",
  decommissioning: "danger",
};

/** NetBox custom fields carrying the compute specs. */
const numericField = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === "") return null;
  const value = typeof raw === "number" ? raw : parseFloat(String(raw));
  return Number.isFinite(value) ? value : null;
};

const deviceSpecs = (
  device: NetBoxDeviceWithInventory,
): { cpuCores: number | null; memoryGb: number | null; storageGb: number | null } => {
  const cf = device.custom_fields ?? {};
  return {
    cpuCores: numericField(cf["vcsa_host_cpu_cores"]),
    memoryGb: numericField(cf["vcsa_host_memory"]),
    storageGb: numericField(cf["Storage"]),
  };
};

/** Interfaces of a device that carry the CPE tag, else the 10GbE fallback. */
const upstreamNics = (
  device: NetBoxDeviceWithInventory,
): { nics: NetBoxInterface[]; tagged: boolean } => {
  const interfaces = device.interfaces ?? [];
  const tagged = interfaces.filter((iface) =>
    iface.tags?.some((tag) => CPE_TAG_SLUGS.includes(tag.slug)),
  );
  if (tagged.length > 0) return { nics: tagged, tagged: true };
  return {
    nics: interfaces.filter((iface) => TEN_GB_TYPES.includes(iface.type?.value)),
    tagged: false,
  };
};

const InterfacesTable = ({
  interfaces,
  labels,
}: {
  interfaces: NetBoxInterface[];
  labels: {
    name: string;
    type: string;
    mac: string;
    state: string;
    up: string;
    down: string;
  };
}): JSX.Element => {
  const columns: Array<EuiBasicTableColumn<NetBoxInterface>> = [
    {
      field: "name",
      name: labels.name,
      render: (name: string, iface: NetBoxInterface) => (
        <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiText size="xs">
              <strong>{name}</strong>
            </EuiText>
          </EuiFlexItem>
          {iface.mgmt_only && (
            <EuiFlexItem grow={false}>
              <EuiBadge color="warning">mgmt</EuiBadge>
            </EuiFlexItem>
          )}
          {iface.tags
            ?.filter((tag) => CPE_TAG_SLUGS.includes(tag.slug))
            .map((tag) => (
              <EuiFlexItem grow={false} key={tag.id}>
                <EuiBadge color="primary">CPE</EuiBadge>
              </EuiFlexItem>
            ))}
        </EuiFlexGroup>
      ),
    },
    {
      field: "type",
      name: labels.type,
      render: (type: NetBoxInterface["type"]) => (
        <EuiText size="xs">{type?.label ?? "-"}</EuiText>
      ),
    },
    {
      field: "mac_address",
      name: labels.mac,
      render: (mac: string | null) => (
        <EuiText size="xs" style={{ fontFamily: "monospace" }}>
          {mac ?? "-"}
        </EuiText>
      ),
    },
    {
      field: "enabled",
      name: labels.state,
      width: "100px",
      render: (enabled: boolean) => (
        <EuiHealth color={enabled ? "success" : "subdued"}>
          <EuiText size="xs">{enabled ? labels.up : labels.down}</EuiText>
        </EuiHealth>
      ),
    },
  ];
  return (
    <EuiBasicTable<NetBoxInterface>
      items={interfaces}
      columns={columns}
      tableLayout="auto"
      compressed
    />
  );
};

const InventoryTable = ({
  inventory,
  labels,
  onCase,
}: {
  inventory: NetBoxInventoryItem[];
  /** Open a hardware case for one component (absent = no permission). */
  onCase?: (item: NetBoxInventoryItem) => void;
  labels: {
    component: string;
    part: string;
    serial: string;
    manufacturer: string;
    discovered: string;
    yes: string;
    no: string;
  };
}): JSX.Element => {
  const columns: Array<EuiBasicTableColumn<NetBoxInventoryItem>> = [
    {
      field: "name",
      name: labels.component,
      render: (name: string, item: NetBoxInventoryItem) => (
        <>
          <EuiText size="xs">
            <strong>{name || "-"}</strong>
          </EuiText>
          {item.description && (
            <EuiText size="xs" color="subdued">
              {item.description}
            </EuiText>
          )}
        </>
      ),
    },
    {
      field: "part_id",
      name: labels.part,
      render: (part: string) => <EuiText size="xs">{part || "-"}</EuiText>,
    },
    {
      field: "serial",
      name: labels.serial,
      render: (serial: string) => (
        <EuiText size="xs" style={{ fontFamily: "monospace" }}>
          {serial || "-"}
        </EuiText>
      ),
    },
    {
      field: "manufacturer",
      name: labels.manufacturer,
      render: (manufacturer: NetBoxInventoryItem["manufacturer"]) => (
        <EuiText size="xs">{manufacturer?.name ?? "-"}</EuiText>
      ),
    },
    {
      field: "discovered",
      name: labels.discovered,
      width: "110px",
      render: (discovered: boolean) => (
        <EuiBadge color={discovered ? "success" : "hollow"}>
          {discovered ? labels.yes : labels.no}
        </EuiBadge>
      ),
    },
    {
      field: "status",
      name: "Status",
      width: "100px",
      render: (status: NetBoxInventoryItem["status"]) =>
        status ? (
          <EuiBadge color={STATUS_COLOR[status.value] ?? "default"}>{status.label}</EuiBadge>
        ) : (
          <EuiText size="xs">-</EuiText>
        ),
    },
    ...(onCase
      ? [
          {
            name: "",
            width: "40px",
            actions: [
              {
                name: "Hardware case",
                description: "Replacement, inspection or return - sets the component to failed in NetBox",
                icon: "wrench",
                type: "icon" as const,
                onClick: (item: NetBoxInventoryItem) => onCase(item),
              },
            ],
          } as EuiBasicTableColumn<NetBoxInventoryItem>,
        ]
      : []),
  ];
  return (
    <EuiBasicTable<NetBoxInventoryItem>
      items={inventory}
      columns={columns}
      tableLayout="auto"
      compressed
    />
  );
};

/**
 * Per-appliance hardware: the NetBox devices selected on the questionnaire
 * (servers, switch, DAC cables) with their full detail - specs, installed
 * components and the uplink NICs.
 *
 * Everything shown comes from NetBox for the ids stored on the questionnaire;
 * ids NetBox no longer knows are reported as missing rather than skipped
 * silently. A customer-supplied switch has no NetBox device, so its manually
 * captured model/MAC/serial is shown instead.
 */
export const OpsanioApplianceFlyoutHardware = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const can = useOpsanioCan();
  // Hardware cases (Replacement / Inspection / Return) for a device or component.
  const canOpenCase = useCanOpenHardwareTicket();
  const [caseTarget, setCaseTarget] = useState<HardwareTicketTarget | null>(null);
  const { translate } = useAnketaI18n();

  const [devices, setDevices] = useState<NetBoxDeviceWithInventory[] | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Memoized so the derived selection/customer-switch memos below don't see a
  // fresh object identity on every render.
  const content = useMemo(() => appliance.content ?? {}, [appliance.content]);

  // The vpc name identifies the appliance to the NetBox sync endpoint; without
  // it there is nothing to sync against, so the button stays hidden.
  const applianceName = appliance.common?.metadata?.name?.trim() ?? "";

  // Device ids per kind, straight off the questionnaire facts.
  const selection = useMemo(() => {
    const serverIds = parseDeviceIds(content[HW_SERVERS_KEY]);
    const switchIds = parseDeviceIds(content[HW_SWITCHES_KEY]);
    const dacIds = parseDeviceIds(content[HW_DAC_KEY]);
    const kindById = new Map<string, OpsanioHardwareKind>();
    serverIds.forEach((id) => kindById.set(id, "server"));
    switchIds.forEach((id) => kindById.set(id, "switch"));
    dacIds.forEach((id) => kindById.set(id, "dac"));
    return {
      serverIds,
      switchIds,
      dacIds,
      kindById,
      allIds: [...serverIds, ...switchIds, ...dacIds],
    };
  }, [content]);

  // A customer-supplied switch is not a NetBox device; the questionnaire keeps
  // its details as plain facts.
  const customerSwitch = useMemo(() => {
    const raw = content[HW_SWITCHES_KEY]?.value ?? content[HW_SWITCHES_KEY];
    const isCustomer =
      typeof raw === "string" && raw.trim() === CUSTOMER_SWITCH_VALUE;
    return {
      isCustomer,
      model: factText(content, HW_SWITCH_MODEL_KEY),
      mac: factText(content, HW_SWITCH_MAC_KEY),
      serial: factText(content, HW_SWITCH_SERIAL_KEY),
    };
  }, [content]);

  const canFetch = useMemo<boolean>(
    () => typeof keycloak?.token === "string" && selection.allIds.length > 0,
    [keycloak, selection],
  );

  const loadDevices = useCallback(async () => {
    if (!canFetch) {
      setDevices([]);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const { success, data, error } = await Get<any>({
        path: `v2/netbox/devices/details?ids=${encodeURIComponent(
          selection.allIds.join(","),
        )}`,
        responseType: "json",
        token: keycloak.token,
      });
      if (!success || error) {
        setLoadError(
          (error as any)?.message ??
            translate(`${i18n}.error.body`, {
              defaultMessage: "NetBox hardware could not be loaded.",
            }),
        );
        setDevices(null);
      } else {
        setDevices(((data as any)?.data as NetBoxDeviceWithInventory[]) ?? []);
      }
    } catch {
      setLoadError(
        translate(`${i18n}.error.body`, {
          defaultMessage: "NetBox hardware could not be loaded.",
        }),
      );
      setDevices(null);
    } finally {
      setLoading(false);
    }
  }, [Get, canFetch, keycloak, selection, translate]);

  useEffect(() => {
    void loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliance.id, canFetch]);

  const kindLabel: Record<OpsanioHardwareKind, string> = {
    server: translate(`${i18n}.kind.server`, { defaultMessage: "Servers" }),
    switch: translate(`${i18n}.kind.switch`, { defaultMessage: "Switches" }),
    dac: translate(`${i18n}.kind.dac`, { defaultMessage: "DAC cables" }),
  };

  const resolved = useMemo(() => devices ?? [], [devices]);
  // Ids on the questionnaire that NetBox did not return (deleted/renumbered).
  const missingIds = useMemo(
    () =>
      selection.allIds.filter(
        (id) => !resolved.some((device) => `${device.id}` === id),
      ),
    [selection, resolved],
  );

  // Honest totals across the resolved servers - fields NetBox does not carry
  // simply contribute nothing (they are not guessed).
  const totals = useMemo(() => {
    let cpuCores = 0;
    let memoryGb = 0;
    let storageGb = 0;
    for (const device of resolved) {
      if (selection.kindById.get(`${device.id}`) !== "server") continue;
      const specs = deviceSpecs(device);
      cpuCores += specs.cpuCores ?? 0;
      memoryGb += specs.memoryGb ?? 0;
      storageGb += specs.storageGb ?? 0;
    }
    return { cpuCores, memoryGb, storageGb };
  }, [resolved, selection]);

  const byKind = (kind: OpsanioHardwareKind): NetBoxDeviceWithInventory[] =>
    resolved.filter((device) => selection.kindById.get(`${device.id}`) === kind);

  /**
   * A switch's 1-based place in the questionnaire's selection, which is what
   * both its NetBox hostname and its reference configuration are numbered from.
   *
   * Read off `selection.switchIds` rather than off the order NetBox happened to
   * return the devices in - a differently ordered response would otherwise hand
   * sws-002's configuration to sws-001. `null` for a device that is not in the
   * list at all, which offers no configuration rather than a guessed one.
   */
  const switchPosition = (device: NetBoxDeviceWithInventory): number | null => {
    const at = selection.switchIds.indexOf(`${device.id}`);
    return at < 0 ? null : at + 1;
  };

  const renderDevice = (
    device: NetBoxDeviceWithInventory,
    kind: OpsanioHardwareKind,
  ): JSX.Element => {
    const specs = deviceSpecs(device);
    const { nics, tagged } = upstreamNics(device);
    const inventory = device.inventory ?? [];

    const meta = [
      {
        title: translate(`${i18n}.field.serial`, {
          defaultMessage: "Serial number",
        }),
        description: device.serial || "-",
      },
      {
        title: translate(`${i18n}.field.model`, { defaultMessage: "Model" }),
        description: `${device.device_type?.manufacturer?.name ?? ""} ${
          device.device_type?.model ?? ""
        }`.trim() || "-",
      },
      {
        title: translate(`${i18n}.field.role`, { defaultMessage: "Role" }),
        description: device.role?.name ?? "-",
      },
      {
        title: translate(`${i18n}.field.site`, { defaultMessage: "Site" }),
        description: device.site?.name ?? "-",
      },
      {
        // Site and tenant must both read `{{vpc-name}}`; showing the tenant is
        // what makes a drifted device visible without opening NetBox.
        title: translate(`${i18n}.field.tenant`, { defaultMessage: "Tenant" }),
        description: (device as any).tenant?.name ?? "-",
      },
      {
        title: translate(`${i18n}.field.platform`, {
          defaultMessage: "Platform",
        }),
        description: device.platform?.name ?? "-",
      },
    ];

    return (
      <EuiPanel hasBorder paddingSize="m" key={device.id}>
        <EuiFlexGroup
          alignItems="center"
          gutterSize="s"
          responsive={false}
          wrap
        >
          <EuiFlexItem grow={false}>
            <EuiIcon type={HARDWARE_KIND_ICON[kind]} size="l" />
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiTitle size="xxs">
              <h4>
                {device.display_url ? (
                  <EuiLink href={device.display_url} target="_blank" external>
                    {device.name || device.display || `#${device.id}`}
                  </EuiLink>
                ) : (
                  (device.name || device.display || `#${device.id}`)
                )}
              </h4>
            </EuiTitle>
            <EuiText size="xs" color="subdued">
              {device.device_type?.model ?? "-"}
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiBadge color={STATUS_COLOR[device.status?.value] ?? "default"}>
              {device.status?.label ?? device.status?.value ?? "-"}
            </EuiBadge>
          </EuiFlexItem>
          {canOpenCase && (
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty
                size="xs"
                iconType="wrench"
                color="warning"
                onClick={() =>
                  setCaseTarget({
                    deviceId: device.id,
                    deviceName: device.name || device.display || `#${device.id}`,
                  })
                }
              >
                {translate("anketa.opsanio.hardware.ticket.action", {
                  defaultMessage: "Hardware case",
                })}
              </EuiButtonEmpty>
            </EuiFlexItem>
          )}
          {/*
            The reference configuration sits on the switch it configures rather
            than on the section: an MLAG pair runs two different configurations
            (own hostname, own CPE management address, own end of the peer
            link), so there is no one "the switch configuration" to offer once
            the customer has two of them. switchPosition is the switch's place
            in the questionnaire's selection - the same place its -m0-sws-00N
            hostname is numbered from.
          */}
          {kind === "switch" &&
            appliance.catalogId &&
            appliance.id &&
            switchPosition(device) !== null && (
              <EuiFlexItem grow={false}>
                <SwitchConfigCopyButton
                  catalogId={appliance.catalogId}
                  questionnaireId={appliance.id}
                  switchIndex={switchPosition(device) as number}
                  deviceName={device.name || device.display || undefined}
                />
              </EuiFlexItem>
            )}
        </EuiFlexGroup>

        <EuiSpacer size="s" />
        <EuiDescriptionList
          type="column"
          compressed
          listItems={meta}
          titleProps={{ style: { width: "40%" } }}
          descriptionProps={{ style: { width: "60%" } }}
        />

        {(specs.cpuCores !== null ||
          specs.memoryGb !== null ||
          specs.storageGb !== null) && (
          <>
            <EuiSpacer size="s" />
            <EuiFlexGroup gutterSize="l" wrap>
              <EuiFlexItem>
                <EuiStat
                  title={
                    specs.cpuCores !== null ? `${specs.cpuCores}` : "-"
                  }
                  description={translate(`${i18n}.stat.cores`, {
                    defaultMessage: "CPU cores",
                  })}
                  titleSize="s"
                />
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiStat
                  title={
                    specs.memoryGb !== null ? `${specs.memoryGb} GiB` : "-"
                  }
                  description={translate(`${i18n}.stat.memory`, {
                    defaultMessage: "Memory",
                  })}
                  titleSize="s"
                />
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiStat
                  title={formatCapacity(specs.storageGb)}
                  description={translate(`${i18n}.stat.storage`, {
                    defaultMessage: "Storage",
                  })}
                  titleSize="s"
                />
              </EuiFlexItem>
            </EuiFlexGroup>
          </>
        )}

        {device.tags?.length > 0 && (
          <>
            <EuiSpacer size="s" />
            <EuiFlexGroup gutterSize="xs" wrap responsive={false}>
              {device.tags.map((tag) => (
                <EuiFlexItem grow={false} key={tag.id}>
                  <EuiBadge color="hollow">{tag.name}</EuiBadge>
                </EuiFlexItem>
              ))}
            </EuiFlexGroup>
          </>
        )}

        {nics.length > 0 && (
          <>
            <EuiSpacer size="s" />
            <EuiAccordion
              id={`hw-nics-${device.id}`}
              buttonContent={
                <EuiText size="xs">
                  <strong>
                    {translate(`${i18n}.nics.title`, {
                      values: { count: nics.length },
                      defaultMessage: "Uplink NICs ({{count}})",
                    })}
                  </strong>{" "}
                  <EuiBadge color={tagged ? "primary" : "warning"}>
                    {tagged
                      ? translate(`${i18n}.nics.tagged`, {
                          defaultMessage: "CPE-tagged",
                        })
                      : translate(`${i18n}.nics.fallback`, {
                          defaultMessage: "10Gb fallback",
                        })}
                  </EuiBadge>
                </EuiText>
              }
            >
              <EuiSpacer size="xs" />
              <InterfacesTable
                interfaces={nics}
                labels={{
                  name: translate(`${i18n}.nics.column.name`, {
                    defaultMessage: "Interface",
                  }),
                  type: translate(`${i18n}.nics.column.type`, {
                    defaultMessage: "Type",
                  }),
                  mac: translate(`${i18n}.nics.column.mac`, {
                    defaultMessage: "MAC address",
                  }),
                  state: translate(`${i18n}.nics.column.state`, {
                    defaultMessage: "State",
                  }),
                  up: translate(`${i18n}.nics.up`, { defaultMessage: "Up" }),
                  down: translate(`${i18n}.nics.down`, {
                    defaultMessage: "Down",
                  }),
                }}
              />
            </EuiAccordion>
          </>
        )}

        {inventory.length > 0 && (
          <>
            <EuiSpacer size="s" />
            <EuiAccordion
              id={`hw-inv-${device.id}`}
              buttonContent={
                <EuiText size="xs">
                  <strong>
                    {translate(`${i18n}.inventory.title`, {
                      values: { count: inventory.length },
                      defaultMessage: "Installed components ({{count}})",
                    })}
                  </strong>
                </EuiText>
              }
            >
              <EuiSpacer size="xs" />
              <InventoryTable
                inventory={inventory}
                onCase={
                  canOpenCase
                    ? (item) =>
                        setCaseTarget({
                          deviceId: device.id,
                          deviceName: device.name || device.display || `#${device.id}`,
                          component: { id: item.id, name: item.name || `#${item.id}` },
                        })
                    : undefined
                }
                labels={{
                  component: translate(`${i18n}.inventory.column.component`, {
                    defaultMessage: "Component",
                  }),
                  part: translate(`${i18n}.inventory.column.part`, {
                    defaultMessage: "Part",
                  }),
                  serial: translate(`${i18n}.inventory.column.serial`, {
                    defaultMessage: "Serial",
                  }),
                  manufacturer: translate(
                    `${i18n}.inventory.column.manufacturer`,
                    { defaultMessage: "Manufacturer" },
                  ),
                  discovered: translate(`${i18n}.inventory.column.discovered`, {
                    defaultMessage: "Discovered",
                  }),
                  yes: translate("anketa.yes", { defaultMessage: "Yes" }),
                  no: translate("anketa.no", { defaultMessage: "No" }),
                }}
              />
            </EuiAccordion>
          </>
        )}
      </EuiPanel>
    );
  };

  if (loading && devices === null) {
    return <FormSkeleton />;
  }

  const nothingSelected =
    selection.allIds.length === 0 && !customerSwitch.isCustomer;

  if (nothingSelected) {
    return (
      <EuiEmptyPrompt
        iconType="storage"
        title={
          <h3>
            {translate(`${i18n}.empty.title`, {
              defaultMessage: "No hardware assigned",
            })}
          </h3>
        }
        body={
          <p>
            {translate(`${i18n}.empty.body`, {
              defaultMessage:
                "This appliance has no NetBox devices selected. Pick servers, a switch and DAC cables in the questionnaire's Hardware section.",
            })}
          </p>
        }
      />
    );
  }

  return (
    <EuiFlexGroup direction="column" gutterSize="m">
      {caseTarget && (
        <HardwareTicketModal
          target={caseTarget}
          onClose={() => setCaseTarget(null)}
          onDone={() => {
            setCaseTarget(null);
            void loadDevices();
          }}
        />
      )}
      <EuiFlexItem>
        <EuiPanel hasBorder paddingSize="m">
          <EuiFlexGroup alignItems="center" gutterSize="m" responsive={false} wrap>
            <EuiFlexItem>
              <EuiTitle size="xs">
                <h3>
                  {translate(`${i18n}.title`, {
                    defaultMessage: "Assigned hardware",
                  })}
                </h3>
              </EuiTitle>
            </EuiFlexItem>
            {/* NetBox sync writes: opsanio/appliance · update. */}
            {applianceName && can(AnketaResourcesEnum.opsanioAppliance, "update") && (
              <EuiFlexItem grow={false}>
                <HardwareSyncButton
                  applianceName={applianceName}
                  onApplied={loadDevices}
                />
              </EuiFlexItem>
            )}
            <EuiFlexItem grow={false}>
              <EuiButton
                size="s"
                iconType="refresh"
                onClick={loadDevices}
                isLoading={loading}
              >
                {translate("anketa.common.refresh", {
                  defaultMessage: "Refresh",
                })}
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>

          <EuiSpacer size="s" />
          <EuiFlexGroup gutterSize="l" wrap>
            <EuiFlexItem>
              <EuiStat
                title={`${byKind("server").length}`}
                description={kindLabel.server}
                titleSize="m"
              />
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiStat
                title={
                  customerSwitch.isCustomer
                    ? translate(`${i18n}.switch.customerShort`, {
                        defaultMessage: "Customer",
                      })
                    : `${byKind("switch").length}`
                }
                description={kindLabel.switch}
                titleSize="m"
              />
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiStat
                title={`${byKind("dac").length}`}
                description={kindLabel.dac}
                titleSize="m"
              />
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiStat
                title={`${totals.cpuCores || "-"}`}
                description={translate(`${i18n}.total.cores`, {
                  defaultMessage: "CPU cores total",
                })}
                titleSize="m"
              />
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiStat
                title={totals.memoryGb ? `${totals.memoryGb} GiB` : "-"}
                description={translate(`${i18n}.total.memory`, {
                  defaultMessage: "Memory total",
                })}
                titleSize="m"
              />
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiStat
                title={
                  totals.storageGb ? formatCapacity(totals.storageGb) : "-"
                }
                description={translate(`${i18n}.total.storage`, {
                  defaultMessage: "Storage total",
                })}
                titleSize="m"
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiPanel>
      </EuiFlexItem>

      {loadError && (
        <EuiFlexItem>
          <EuiCallOut
            size="s"
            color="danger"
            iconType="alert"
            title={translate(`${i18n}.error.title`, {
              defaultMessage: "NetBox hardware could not be loaded",
            })}
          >
            <p>{loadError}</p>
            <EuiButton size="s" color="danger" iconType="refresh" onClick={loadDevices}>
              {translate("anketa.common.retry", { defaultMessage: "Retry" })}
            </EuiButton>
          </EuiCallOut>
        </EuiFlexItem>
      )}

      {missingIds.length > 0 && (
        <EuiFlexItem>
          <EuiCallOut
            size="s"
            color="warning"
            iconType="questionInCircle"
            title={translate(`${i18n}.missing.title`, {
              values: { ids: missingIds.join(", ") },
              defaultMessage:
                "NetBox returned no device for id(s) {{ids}} - selected on the questionnaire but not found",
            })}
          />
        </EuiFlexItem>
      )}

      {customerSwitch.isCustomer && (
        <EuiFlexItem>
          <EuiCallOut
            size="s"
            color="primary"
            iconType="iInCircle"
            title={translate(`${i18n}.switch.customer`, {
              defaultMessage: "Customer-supplied switch (not in NetBox)",
            })}
          >
            <EuiDescriptionList
              type="column"
              compressed
              listItems={[
                {
                  title: translate(`${i18n}.field.model`, {
                    defaultMessage: "Model",
                  }),
                  description: customerSwitch.model || "-",
                },
                {
                  title: translate(`${i18n}.field.mac`, {
                    defaultMessage: "MAC address",
                  }),
                  description: customerSwitch.mac || "-",
                },
                {
                  title: translate(`${i18n}.field.serial`, {
                    defaultMessage: "Serial number",
                  }),
                  description: customerSwitch.serial || "-",
                },
              ]}
            />
          </EuiCallOut>
        </EuiFlexItem>
      )}

      {(["server", "switch", "dac"] as OpsanioHardwareKind[]).map((kind) => {
        const kindDevices = byKind(kind);
        if (kindDevices.length === 0) return null;
        return (
          <EuiFlexItem key={kind}>
            <EuiTitle size="xxs">
              <h4>
                {kindLabel[kind]} ({kindDevices.length})
              </h4>
            </EuiTitle>
            <EuiHorizontalRule margin="xs" />
            <EuiFlexGroup direction="column" gutterSize="s">
              {kindDevices.map((device) => (
                <EuiFlexItem key={device.id}>
                  {renderDevice(device, kind)}
                </EuiFlexItem>
              ))}
            </EuiFlexGroup>
          </EuiFlexItem>
        );
      })}
    </EuiFlexGroup>
  );
};

export default OpsanioApplianceFlyoutHardware;
